import { useState, useEffect, useCallback } from "react";
import { suppliesService } from "../services/suppliesService";

export function useSupplyPurchases() {
  const [purchases, setPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 1. Busca todos os pedidos de compra
  const fetchPurchases = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await suppliesService.getPurchases();
      setPurchases(data);
    } catch (err) {
      console.error("Erro ao buscar pedidos de compra:", err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Carrega ao montar
  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  // 2. Criar Novo Pedido de Compras (Draft)
  const createPurchase = async (items) => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        status: "draft",
        items: items, // JSON
      };
      const record = await suppliesService.savePurchase(null, payload);
      await fetchPurchases();
      return record;
    } catch (err) {
      console.error("Erro ao criar pedido de compras:", err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // 3. Salvar Alterações no Rascunho
  const updatePurchase = async (id, items) => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        items: items,
      };
      const record = await suppliesService.savePurchase(id, payload);
      await fetchPurchases();
      return record;
    } catch (err) {
      console.error("Erro ao atualizar pedido de compras:", err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // 4. Excluir Rascunho de Compra
  const removePurchase = async (id) => {
    setIsLoading(true);
    setError(null);
    try {
      await suppliesService.deletePurchase(id);
      await fetchPurchases();
    } catch (err) {
      console.error("Erro ao excluir pedido de compras:", err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // 5. Transicionar de Draft para Ordered (Confirmar Envio Financeiro/Pedido Efetuado)
  const markAsOrdered = async (id, updatedItems) => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        status: "ordered",
      };
      if (updatedItems) {
        payload.items = updatedItems;
      }
      const record = await suppliesService.savePurchase(id, payload);
      await fetchPurchases();
      return record;
    } catch (err) {
      console.error("Erro ao marcar como comprado:", err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // 6. Finalizar Recebimento e Incrementar Saldos no PocketBase (Atomic Increment)
  const receivePurchase = async (id, itemsWithReceivedQty, onProgress) => {
    setIsLoading(true);
    setError(null);
    try {
      // 1. Primeiro atualiza as quantidades recebidas no banco
      const payload = {
        status: "received",
        items: itemsWithReceivedQty,
      };
      const record = await suppliesService.savePurchase(id, payload);

      // 2. Atualiza o estoque real chamando o adjustCurrentQuantity (Numeric Delta atômico)
      const totalItems = itemsWithReceivedQty.length;
      for (let i = 0; i < totalItems; i++) {
        const item = itemsWithReceivedQty[i];
        const receivedQty = Number(item.received_quantity) || 0;
        
        if (onProgress) {
          onProgress(item.name, i + 1, totalItems);
        }

        if (receivedQty > 0) {
          const packFactor = Number(item.unidades_por_compra) || 1;
          const totalAdd = receivedQty * packFactor;
          // Incrementa atomicamente no saldo físico do insumo
          await suppliesService.adjustCurrentQuantity(item.supply_id, totalAdd);
        }
      }

      await fetchPurchases();
      return record;
    } catch (err) {
      console.error("Erro ao confirmar recebimento de compras:", err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    purchases,
    isLoading,
    error,
    fetchPurchases,
    createPurchase,
    updatePurchase,
    removePurchase,
    markAsOrdered,
    receivePurchase,
  };
}
